import { useState, useRef, useEffect, useCallback } from "react";
import {
  useDeleteApiPhotosByIdMutation,
  usePostApiPhotosMutation,
} from "@shared/api";
import { useAutoCloseByStatus } from "@shared/utils";
import * as Yup from "yup";
import { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const fileSchema = Yup.mixed()
  .test(
    "fileSize",
    "Файл слишком большой. Максимальный размер 10MB",
    (value: any) => {
      if (!value) return true;
      const file = value as File;
      return file.size <= MAX_FILE_SIZE;
    }
  )
  .test(
    "fileFormat",
    "Формат файла не поддерживается. Разрешены: jpg, jpeg, png, gif, heif, heic",
    (value: any) => {
      if (!value) return true;
      const file = value as File;
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/heif",
        "image/heic",
      ];
      return validTypes.includes(file.type.toLowerCase());
    }
  );

const cropImage = (
  image: HTMLImageElement,
  crop: PixelCrop,
  fileName: string
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const MIN_SIZE = 300;
    const cropWidth = crop.width;
    const cropHeight = crop.height;

    const width = Math.max(cropWidth, MIN_SIZE);
    const height = Math.max(cropHeight, MIN_SIZE);

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.clearRect(0, 0, width, height);

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      cropWidth * scaleX,
      cropHeight * scaleY,
      0,
      0,
      width,
      height
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not convert to PNG"));
          return;
        }

        const file = new File([blob], fileName, {
          type: "image/png",
          lastModified: Date.now(),
        });
        resolve(file);
      },
      "image/png",
      1.0
    );
  });
};

const convertToPNG = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");

          const MIN_SIZE = 150;
          const MAX_WIDTH = 1000;
          const MAX_HEIGHT = 1000;

          let width = img.width;
          let height = img.height;

          let compressionQuality = 1.0;
          const originalSize = file.size;

          if (originalSize > 5 * 1024 * 1024) {
            // Более 5MB
            compressionQuality = 0.9;
          } else if (originalSize > 2 * 1024 * 1024) {
            // Более 2MB
            compressionQuality = 0.9;
          } else if (originalSize > 1 * 1024 * 1024) {
            // Более 1MB
            compressionQuality = 0.9;
          }

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }
          }

          width = Math.max(width, MIN_SIZE);
          height = Math.max(height, MIN_SIZE);

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
          }

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, width, height);

          const outputFormat = file.type.includes("image/png")
            ? "image/png"
            : "image/jpeg";

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Could not convert image"));
                return;
              }

              const extension = outputFormat === "image/png" ? ".png" : ".jpg";
              const newFile = new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, extension),
                {
                  type: outputFormat,
                  lastModified: Date.now(),
                }
              );

              resolve(newFile);
            },
            outputFormat,
            compressionQuality
          );
        } catch (error) {
          reject(
            new Error("Failed to process image: " + (error as Error).message)
          );
        }
      };

      img.onerror = () => {
        reject(
          new Error(
            "Failed to load image. The file might be corrupted or in an unsupported format."
          )
        );
      };

      if (!e.target?.result) {
        reject(new Error("Failed to read file data"));
        return;
      }

      img.src = e.target.result as string;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file. The file might be corrupted."));
    };
    reader.readAsDataURL(file);
  });
};

interface UseUploadFormProps {
  isOpen: boolean;
  onClose: () => void;
  currentAvatar: string;
  currentPhotoId?: string;
  onSave?: () => void;
}

export const useUploadForm = ({
  isOpen,
  onClose,
  currentAvatar,
  currentPhotoId,
  onSave,
}: UseUploadFormProps) => {
  const [postPhoto] = usePostApiPhotosMutation();
  const [deletePhoto] = useDeleteApiPhotosByIdMutation();
  const [status, setStatus] = useState<"success" | "error" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [files, setFiles] = useState<FileList | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const isUploadingRef = useRef<boolean>(false);
  const currentFileIdRef = useRef<string | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: "px" as const,
    width: 150,
    height: 150,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [isCropping, setIsCropping] = useState<boolean>(false);
  const [wasCropped, setWasCropped] = useState<boolean>(false);

  useAutoCloseByStatus(status, setStatus, onClose);

  useEffect(() => {
    if (currentAvatar) {
      setPreview(currentAvatar);
    }
  }, [currentAvatar, isOpen]);

  useEffect(() => {
    if (!files || files.length === 0) return;

    if (!wasCropped) {
      setIsCropping(true);
    }
  }, [files, wasCropped]);

  useEffect(() => {
    if (!isOpen) {
      setFiles(null);
      setCroppedFile(null);
      setValidationError(null);
      setIsCropping(false);
      setCompletedCrop(null);
      setWasCropped(false);
      setErrorMessage(null);
    } else {
      setStatus(null);
      setErrorMessage(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const currentImg = imgRef.current;
    if (isCropping && currentImg) {
      const handleImageLoad = () => {
        const centeredX = Math.max(0, (currentImg.width - 150) / 2);
        const centeredY = Math.max(0, (currentImg.height - 150) / 2);

        setCrop((oldCrop) => ({
          ...oldCrop,
          x: centeredX,
          y: centeredY,
        }));

        setCompletedCrop({
          x: centeredX,
          y: centeredY,
          width: 150,
          height: 150,
          unit: "px",
        });
      };

      if (currentImg.complete) {
        handleImageLoad();
      } else {
        currentImg.addEventListener("load", handleImageLoad);
        return () => {
          currentImg.removeEventListener("load", handleImageLoad);
        };
      }
    }
  }, [isCropping]);

  const handleFileSelect = async (file: File | null) => {
    setValidationError(null);
    setWasCropped(false);

    if (!file) {
      return;
    }

    try {
      await fileSchema.validate(file);

      const fileId = `${file.name}-${file.size}-${file.lastModified}`;
      currentFileIdRef.current = fileId;

      let processedFile = file;

      if (
        file.name.toLowerCase().endsWith(".heic") ||
        file.name.toLowerCase().endsWith(".heif")
      ) {
        if (file.type !== "image/heic" && file.type !== "image/heif") {
          const newFile = new File([file], file.name, {
            type: file.name.toLowerCase().endsWith(".heic")
              ? "image/heic"
              : "image/heif",
            lastModified: file.lastModified,
          });
          processedFile = newFile;
        }
      }

      const img = new Image();
      const imgLoadPromise = new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () =>
          reject(new Error("Invalid or corrupted image file"));
      });

      const fileReader = new FileReader();
      const readerPromise = new Promise((resolve, reject) => {
        fileReader.onload = resolve;
        fileReader.onerror = () => reject(new Error("Failed to read file"));
      });

      fileReader.readAsDataURL(processedFile);
      img.src = URL.createObjectURL(processedFile);

      await Promise.all([imgLoadPromise, readerPromise]);

      const fileArray = new Array(1);
      fileArray[0] = processedFile;

      setFiles(fileArray as unknown as FileList);
      setPreview(fileReader.result as string);

      URL.revokeObjectURL(img.src);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setValidationError(error.message);
      } else {
        setValidationError((error as Error).message);
        console.error("File selection error:", error);
      }
    }
  };

  const cancelCrop = () => {
    setIsCropping(false);
  };

  const applyCrop = async () => {
    if (!imgRef.current || !files) {
      return;
    }

    try {
      const cropToApply = completedCrop || {
        x: crop.x,
        y: crop.y,
        width: crop.width,
        height: crop.height,
        unit: "px",
      };

      const croppedFile = await cropImage(
        imgRef.current,
        cropToApply as PixelCrop,
        files[0].name.replace(/\.[^/.]+$/, ".png")
      );

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(croppedFile);

      setCroppedFile(croppedFile);
      setIsCropping(false);
      setWasCropped(true);
    } catch (error) {
      console.error("Ошибка при обрезке изображения:", error);
      setValidationError("Не удалось обрезать изображение");
    }
  };

  const handleSave = useCallback(async () => {
    if (isUploadingRef.current) {
      return;
    }

    try {
      if (!files || files.length === 0) {
        onClose();
        return;
      }

      isUploadingRef.current = true;
      setIsLoading(true);

      const formData = new FormData();
      const originalFile = files[0];

      if (!originalFile) {
        console.error("Missing file data when trying to save");
        throw new Error("Ошибка при загрузке файла: данные файла отсутствуют");
      }

      if (preview) {
        URL.revokeObjectURL(preview);
      }

      let fullImage = await convertToPNG(originalFile);

      let shortcutImage;
      if (wasCropped && croppedFile) {
        shortcutImage = await convertToPNG(croppedFile);
      } else {
        shortcutImage = fullImage;
      }

      if (!fullImage || !shortcutImage) {
        console.error("Error converting images", { fullImage, shortcutImage });
        throw new Error("Ошибка при обработке изображения");
      }

      formData.append("image", fullImage);
      formData.append("shortcut_image", shortcutImage);

      await postPhoto({
        body: formData,
      }).unwrap();

      if (currentPhotoId) {
        try {
          await deletePhoto({ id: currentPhotoId }).unwrap();
        } catch (deleteError) {
          console.error("Error deleting old photo:", deleteError);
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      setErrorMessage(null);
      setStatus("success");

      if (onSave) {
        setTimeout(() => {
          onSave();
        }, 500);
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      setErrorMessage(
        "Произошла непредвиденная ошибка, попробуйте снова или выберите другое фото"
      );
    } finally {
      setIsLoading(false);
      isUploadingRef.current = false;
    }
  }, [
    files,
    croppedFile,
    wasCropped,
    onSave,
    onClose,
    preview,
    currentPhotoId,
    postPhoto,
    deletePhoto,
  ]);

  useEffect(() => {
    return () => {
      isUploadingRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (files && files[0]) {
      const newFileId = `${files[0].name}-${files[0].size}-${files[0].lastModified}`;
      if (currentFileIdRef.current !== newFileId) {
        isUploadingRef.current = false;
        currentFileIdRef.current = newFileId;
      }
    }
  }, [files]);

  const saveFunction = useCallback(() => {
    if (!isUploadingRef.current) {
      handleSave();
    }
  }, [handleSave]);

  useEffect(() => {
    if (!isOpen || status === "success" || status === "error") return;

    const tg = window.Telegram.WebApp;

    if (files && files.length > 0 && !isCropping) {
      try {
        try {
          tg.MainButton.offClick(saveFunction);
        } catch (err) {
          console.error(
            "Failed to pass callback to offClick, trying without arguments"
          );
        }
      } catch (e) {
        console.error("Error clearing button handlers:", e);
      }

      tg.MainButton.setText("Сохранить фото");
      tg.MainButton.onClick(saveFunction);

      if (!tg.MainButton.isVisible) {
        tg.MainButton.show();
      }
    } else {
      tg.MainButton.hide();
    }

    return () => {
      try {
        tg.MainButton.offClick(saveFunction);
        tg.MainButton.hide();
      } catch (e) {
        console.error("Error cleaning up button:", e);
      }
    };
  }, [isOpen, status, files, isCropping]);

  return {
    status,
    setStatus,
    files,
    preview,
    handleFileSelect,
    handleSave,
    validationError,
    isLoading,
    imgRef,
    crop,
    setCrop,
    completedCrop,
    setCompletedCrop,
    isCropping,
    setIsCropping,
    cancelCrop,
    applyCrop,
    wasCropped,
    errorMessage,
  };
};
